import { useQueryClient } from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Dropdown,
  Flex,
  Space,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import type { TableColumnsType } from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import {
  Archive,
  ArchiveRestore,
  Ban,
  Download,
  Eye,
  Mail,
  MoreHorizontal,
  Pencil,
  Plus,
  StopCircle,
  Tags,
  Trash2,
  Undo2
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import type { OrganizationsAccessListData } from 'src/api/main'
import { organizationsAccessListOptions } from 'src/api/main'
import { OrganizationRole } from 'src/api/main'
import {
  ActiveFilterTags,
  BulkActionsBar,
  ContentSpinner,
  DateTimeCell,
  ProTableContainer,
  QueryErrorAlert,
  SourceTag,
  TableActionColumnTitle,
  UserCell
} from 'src/components'
import type { BulkAction as BulkActionBarAction } from 'src/components'
import { FilterLabelsProvider } from 'src/context'
import {
  usePaginationConfig,
  useQueryParams,
  useQueryUpdate,
  useTableSorter,
  useHasWorkspacePermissions
} from 'src/hooks'
import { paths } from 'src/router'
import {
  defaultFilters,
  defaultFiltersMultipleValues,
  downloadCsv,
  formatDateTime,
  formatRelative,
  type ActiveFilterConfig,
  type CsvColumn,
  WorkspacePermissionCode
} from 'src/utils'

import { InviteUserDrawer } from '../components/InviteUserDrawer'
import { MemberRoleTag } from '../components/MemberRoleTag'
import { UserArchiveModal } from '../components/UserArchiveModal'
import {
  UserBulkActionModal,
  type BulkAction
} from '../components/UserBulkActionModal'
import { UserChangeRoleModal } from '../components/UserChangeRoleModal'
import { UserConfirmActionModal } from '../components/UserConfirmActionModal'
import { UserDeleteModal } from '../components/UserDeleteModal'
import { UserEditDrawer } from '../components/UserEditDrawer'
import { UserResendInviteModal } from '../components/UserResendInviteModal'
import { UsersFilterControls } from '../components/UsersFilterBar'
import { UsersStatusPill } from '../components/UsersStatusPill'
import { UserSuspendModal } from '../components/UserSuspendModal'
import { WorkspaceJoinLinkButton } from '../components/WorkspaceJoinLinkButton'
import { useWorkspaceUsersList } from '../hooks'
import type {
  InviteState,
  UserStatus,
  UserStatusFilter,
  WorkspaceUser
} from '../types'
import {
  composeDisplayName,
  deriveInviteState,
  deriveUserStatus
} from '../utils'

const { Text } = Typography

type RowAction =
  | 'edit'
  | 'changeRole'
  | 'suspend'
  | 'reactivate'
  | 'archive'
  | 'unarchive'
  | 'delete'
  | 'resend'
  | 'revokeInvite'

// Export pulls every matching row for the CSV, not just the current page. 500
// matches the app-wide "fetch all" page size used by the org chart / teams
// hooks.
type ExportStatusFilter = NonNullable<
  OrganizationsAccessListData['query']
>['status']

const ALL_USERS_PER_PAGE = 500

const WorkspaceUsersPageInner = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id: idParam } = useParams<{ id: string }>()
  const workspaceId = Number(idParam)
  const valid = Number.isFinite(workspaceId) && workspaceId > 0
  const { message } = AntdApp.useApp()

  const params = useQueryParams()
  const { updateQuery } = useQueryUpdate()
  const sorter = useTableSorter()

  const page = Number(params.page) || defaultFilters.page
  const perPage =
    Number(params.page_size ?? params.per_page) || defaultFilters.per_page
  const searchTerm = String(params.search ?? '')
  const orderingParam = String(params.ordering ?? '')
  const teamFilter = defaultFiltersMultipleValues(params.team, true) ?? []
  const statusFilter = params.status as UserStatusFilter | undefined
  const joinedAfter = String(params.joined_date_after ?? '')
  const joinedBefore = String(params.joined_date_before ?? '')

  const setFilter = (patch: Record<string, unknown>) =>
    updateQuery({ ...params, page: defaultFilters.page, ...patch })

  const joinedRange = useMemo<[Dayjs | null, Dayjs | null] | null>(() => {
    const after = joinedAfter ? dayjs(joinedAfter) : null
    const before = joinedBefore ? dayjs(joinedBefore) : null

    return after || before ? [after, before] : null
  }, [joinedAfter, joinedBefore])

  /**
   * `joined_date` is a DRF `DateFromToRangeFilter`, so both bounds are plain
   * calendar dates (`YYYY-MM-DD`) and inclusive — not timestamps. Formatting
   * here is query-param serialisation, not display; user-facing dates still go
   * through `DateTimeCell` / the `dateTime` helpers.
   */
  const handleJoinedRangeChange = (
    next: [Dayjs | null, Dayjs | null] | null
  ) => {
    setFilter({
      joined_date_after: next?.[0] ? next[0].format('YYYY-MM-DD') : null,
      joined_date_before: next?.[1] ? next[1].format('YYYY-MM-DD') : null
    })
  }

  const { users, isLoading, isFetching, isError, refetch, total } =
    useWorkspaceUsersList({
      workspaceId,
      enabled: valid,
      query: {
        page,
        per_page: perPage,
        search: searchTerm || undefined,
        ordering: orderingParam || undefined,
        team: teamFilter.length ? teamFilter : undefined,
        status: statusFilter,
        joined_date_after: joinedAfter || undefined,
        joined_date_before: joinedBefore || undefined
      }
    })

  const filterTagItems: ActiveFilterConfig[] = [
    { type: 'select', key: 'team', label: t('users.filters.teamChip') },
    { type: 'select', key: 'status', label: t('users.filters.statusChip') },
    {
      type: 'dateRange',
      key: 'joined_date_after',
      from: 'joined_date_after',
      to: 'joined_date_before',
      label: t('users.filters.joinedChip'),
      clearKeys: ['joined_date_after', 'joined_date_before']
    }
  ]

  const [selected, setSelected] = useState<number[]>([])
  const [inviteOpen, setInviteOpen] = useState(false)
  const [canInviteMembers, canManageMembers, canManageRoles] =
    useHasWorkspacePermissions([
      WorkspacePermissionCode.USERS_INVITE,
      WorkspacePermissionCode.USERS_MANAGE,
      WorkspacePermissionCode.ROLES_MANAGE
    ])
  const [activeAction, setActiveAction] = useState<{
    action: RowAction
    user: WorkspaceUser
  } | null>(null)
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null)

  const openAction = (action: RowAction, user: WorkspaceUser) =>
    setActiveAction({ action, user })
  const closeAction = () => setActiveAction(null)

  const rows = useMemo(
    () =>
      users.map((u) => ({
        record: u,
        status: deriveUserStatus(u),
        inviteState: deriveInviteState(u)
      })),
    [users]
  )

  const goToUser = (userId: number) => {
    navigate(
      paths.workspaces.membersPerson(String(workspaceId), String(userId))
    )
  }

  const clearAll = () => {
    updateQuery({}, true)
  }

  const paginationConfig = usePaginationConfig({
    count: total,
    results: rows
  })

  const handleExport = async () => {
    // Export the full matching set, not just the current server page.
    const res = await queryClient.fetchQuery(
      organizationsAccessListOptions({
        query: {
          organization: workspaceId,
          per_page: ALL_USERS_PER_PAGE,
          search: searchTerm || undefined,
          joined_date_after: joinedAfter || undefined,
          joined_date_before: joinedBefore || undefined,
          ordering: orderingParam || undefined,
          team: teamFilter.length ? teamFilter : undefined,
          ...(statusFilter
            ? { status: statusFilter as unknown as ExportStatusFilter }
            : {})
        }
      })
    )
    const exportRows = (res.results ?? []).map((record) => ({
      record,
      status: deriveUserStatus(record)
    }))
    if (exportRows.length === 0) {
      message.info(t('users.export.empty'))
      return
    }
    type Row = (typeof exportRows)[number]
    const columns: Array<CsvColumn<Row>> = [
      {
        header: t('users.columns.user'),
        value: ({ record }) =>
          composeDisplayName(
            record.user.first_name,
            record.user.last_name,
            record.user.email
          )
      },
      {
        header: t('users.columns.email'),
        value: ({ record }) => record.user.email
      },
      { header: t('users.columns.role'), value: ({ record }) => record.role },
      { header: t('users.columns.status'), value: ({ status }) => status },
      {
        header: t('users.columns.jobTitle'),
        value: ({ record }) => record.job_title ?? ''
      },
      {
        header: t('users.columns.lastSignIn'),
        value: ({ record }) =>
          record.user.last_login ? formatDateTime(record.user.last_login) : ''
      },
      {
        header: t('users.columns.joined'),
        value: ({ record }) => formatDateTime(record.joined_date)
      }
    ]
    const stamp = new Date().toISOString().slice(0, 10)
    downloadCsv(`workspace-${workspaceId}-users-${stamp}`, exportRows, columns)
    message.success(t('users.export.success', { count: exportRows.length }))
  }

  // Column `key` values are sent to the API as DRF `ordering`. They must
  // match the names whitelisted in `OrderingFilter.ordering_fields` on
  // `/organizations/access/` — if a name isn't allowed, the request goes
  // through but the response order is unchanged.
  const columns: TableColumnsType<{
    record: WorkspaceUser
    status: UserStatus
    inviteState: InviteState | null
  }> = [
    {
      title: t('users.columns.user'),
      key: 'first_name',
      fixed: 'left',
      dataIndex: ['record', 'user', 'first_name'],
      width: 260,
      sorter: true,
      render: (_, { record }) => (
        <UserCell
          firstName={record.user.first_name}
          lastName={record.user.last_name}
          email={record.user.email}
          photoUrl={record.user.photo?.file ?? null}
          size="md"
          onClick={() => goToUser(record.user.id)}
        />
      )
    },
    {
      title: t('users.columns.role'),
      key: 'role',
      dataIndex: ['record', 'role'],
      width: 120,
      sorter: true,
      render: (_, { record }) => <MemberRoleTag record={record} />
    },
    {
      title: t('users.columns.status'),
      key: 'status',
      dataIndex: 'status',
      width: 170,
      render: (_, { status, inviteState, record }) => {
        const pill = (
          <UsersStatusPill status={status} inviteState={inviteState} />
        )
        const expiresAt = record.invite_expires_at
        if (
          expiresAt &&
          (inviteState === 'pending' || inviteState === 'expired')
        ) {
          const key =
            inviteState === 'expired'
              ? 'users.lifecycle.inviteExpired'
              : 'users.lifecycle.inviteExpires'

          return (
            <Tooltip title={t(key, { when: formatRelative(expiresAt) })}>
              <span>{pill}</span>
            </Tooltip>
          )
        }

        return pill
      }
    },
    {
      title: t('users.columns.jobTitle'),
      key: 'job_title',
      dataIndex: ['record', 'job_title'],
      width: 200,
      ellipsis: true,
      render: (v: string | null) =>
        v ? (
          <Text style={{ fontSize: 12 }} ellipsis={{ tooltip: v }}>
            {v}
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            —
          </Text>
        )
    },
    {
      title: t('users.columns.manager'),
      key: 'head_user',
      width: 220,
      ellipsis: true,
      render: (_, { record }) => {
        const m = record.head_user

        return m ? (
          <UserCell
            firstName={m.first_name}
            lastName={m.last_name}
            email={m.email}
            size="sm"
            onClick={() => goToUser(m.id)}
          />
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            —
          </Text>
        )
      }
    },
    {
      title: t('users.columns.teams'),
      key: 'teams',
      width: 200,
      render: (_, { record }) => {
        const memberTeams = record.teams ?? []
        if (memberTeams.length === 0)
          return (
            <Text type="secondary" style={{ fontSize: 12 }}>
              —
            </Text>
          )
        const rest = memberTeams.slice(2)

        return (
          <Space size={[4, 4]} wrap>
            {memberTeams.slice(0, 2).map((tm) => (
              <Tag key={tm.id} style={{ marginInlineEnd: 0, fontSize: 11 }}>
                {tm.name}
              </Tag>
            ))}
            {rest.length > 0 ? (
              <Tooltip title={rest.map((tm) => tm.name).join(', ')}>
                <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>
                  +{rest.length}
                </Tag>
              </Tooltip>
            ) : null}
          </Space>
        )
      }
    },
    {
      title: t('users.columns.mfa'),
      key: 'mfa',
      width: 110,
      // mfa_enabled is null for non-admin viewers (admin-only field) — show a
      // muted dash rather than a misleading "Disabled".
      render: (_, { record }) =>
        record.mfa_enabled === null ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            —
          </Text>
        ) : record.mfa_enabled ? (
          <Tag color="success" style={{ marginInlineEnd: 0, fontSize: 11 }}>
            {t('users.columns.mfaEnabled')}
          </Tag>
        ) : (
          <Tag style={{ marginInlineEnd: 0, fontSize: 11 }}>
            {t('users.columns.mfaDisabled')}
          </Tag>
        )
    },
    {
      title: t('users.columns.source'),
      key: 'source',
      width: 110,
      render: (_, { record }) => <SourceTag ssoId={record.user.sso_id} />
    },
    {
      title: t('users.columns.lastSignIn'),
      key: 'last_login',
      dataIndex: ['record', 'user', 'last_login'],
      width: 140,
      sorter: true,
      render: (v: string | null | undefined) => (
        <DateTimeCell
          value={v}
          variant="relative"
          empty={t('users.lastSignIn.never')}
          style={{ fontSize: 12 }}
        />
      )
    },
    {
      title: t('users.columns.joined'),
      key: 'joined_date',
      dataIndex: ['record', 'joined_date'],
      width: 130,
      sorter: true,
      render: (v: string) => (
        <DateTimeCell value={v} variant="date" style={{ fontSize: 12 }} />
      )
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 56,
      align: 'center' as const,
      fixed: 'right' as const,
      render: (_, { record, status, inviteState }) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              {
                key: 'view',
                icon: <Eye size={14} />,
                label: t('users.actions.view'),
                onClick: () => goToUser(record.user.id)
              },
              ...(canManageMembers
                ? [
                    {
                      key: 'edit',
                      icon: <Pencil size={14} />,
                      label: t('users.actions.edit'),
                      onClick: () => openAction('edit', record)
                    }
                  ]
                : []),
              // The owner's role is not reassignable from here — handing the
              // workspace over is its own flow in the danger zone. Assigning
              // roles answers to `roles.manage`, not to `users.manage`.
              ...(canManageRoles && record.role !== OrganizationRole.OWNER
                ? [
                    {
                      key: 'changeRole',
                      icon: <Tags size={14} />,
                      label: t('users.actions.changeRole'),
                      onClick: () => openAction('changeRole', record)
                    }
                  ]
                : []),
              ...(canInviteMembers
                ? [
                    {
                      key: 'resend',
                      icon: <Mail size={14} />,
                      label: t('users.actions.resendInvite'),
                      // Available while the invite is unaccepted (incl. revoked, to re-send).
                      disabled: !inviteState || inviteState === 'accepted',
                      onClick: () => openAction('resend', record)
                    },
                    {
                      key: 'revokeInvite',
                      icon: <Ban size={14} />,
                      label: t('users.lifecycle.revokeInvite.action'),
                      danger: true,
                      // Only a still-pending/expired invite can be revoked.
                      disabled:
                        inviteState !== 'pending' && inviteState !== 'expired',
                      onClick: () => openAction('revokeInvite', record)
                    }
                  ]
                : []),
              ...(canManageMembers
                ? [
                    { type: 'divider' as const },
                    status === 'suspended'
                      ? {
                          key: 'restore',
                          icon: <Undo2 size={14} />,
                          label: t('users.actions.reactivate'),
                          // Suspension toggles the `blocked` flag, which the
                          // backend only allows once the invite is accepted.
                          disabled: inviteState !== 'accepted',
                          onClick: () => openAction('reactivate', record)
                        }
                      : {
                          key: 'suspend',
                          icon: <StopCircle size={14} />,
                          label: t('users.actions.suspend'),
                          disabled: inviteState !== 'accepted',
                          onClick: () => openAction('suspend', record)
                        },
                    // Archive/unarchive shares the backend `blocked` flag with
                    // suspend, so it keys off the same derived status: a
                    // suspended (blocked) member can only be unarchived,
                    // everyone else archived.
                    status === 'suspended'
                      ? {
                          key: 'unarchive',
                          icon: <ArchiveRestore size={14} />,
                          label: t('users.actions.unarchive'),
                          onClick: () => openAction('unarchive', record)
                        }
                      : {
                          key: 'archive',
                          icon: <Archive size={14} />,
                          label: t('users.actions.archive'),
                          disabled: inviteState !== 'accepted',
                          onClick: () => openAction('archive', record)
                        },
                    {
                      key: 'delete',
                      icon: <Trash2 size={14} />,
                      label: t('users.actions.delete'),
                      danger: true,
                      onClick: () => openAction('delete', record)
                    }
                  ]
                : [])
            ]
          }}
        >
          <Button
            type="text"
            size="small"
            icon={<MoreHorizontal size={14} />}
            aria-label={t('users.actions.more')}
          />
        </Dropdown>
      )
    }
  ]

  const bulkActions: BulkActionBarAction[] = [
    {
      title: t('users.actions.suspend'),
      buttonProps: {
        icon: <StopCircle />,
        onClick: () => setBulkAction('suspend')
      }
    },
    {
      title: t('users.actions.delete'),
      buttonProps: {
        icon: <Trash2 />,
        danger: true,
        onClick: () => setBulkAction('delete')
      }
    }
  ]

  if (!valid) return null
  if (isLoading) return <ContentSpinner minHeight={400} />
  if (isError) return <QueryErrorAlert onRetry={() => void refetch()} />

  return (
    <Flex vertical gap={14}>
      <ProTableContainer
        tableIdentifier="workspace-users"
        searchPlaceholder={t('users.filters.searchPlaceholder')}
        loading={isFetching}
        columns={columns}
        dataSource={rows}
        rowKey={(row) => row.record.id}
        rowSelection={
          canManageMembers
            ? {
                selectedRowKeys: selected,
                onChange: (keys) => setSelected(keys.map(Number))
              }
            : undefined
        }
        pagination={paginationConfig}
        onChange={(_, __, sorterResult) => {
          setSelected([])
          sorter(sorterResult)
        }}
        showSorterTooltip={false}
        scroll={{ x: 1700 }}
        tableLayout="fixed"
        leftChildren={
          <>
            <UsersFilterControls
              team={teamFilter}
              onTeamChange={(v) => setFilter({ team: v.length ? v : null })}
              status={statusFilter}
              onStatusChange={(v) => setFilter({ status: v })}
              joinedRange={joinedRange}
              onJoinedRangeChange={handleJoinedRangeChange}
              workspaceId={workspaceId}
            />

            <Flex flex="1 0 100%">
              <ActiveFilterTags items={filterTagItems} onClearAll={clearAll} />
            </Flex>
          </>
        }
        rightChildren={
          <Space size="small">
            {canInviteMembers ? (
              <WorkspaceJoinLinkButton workspaceId={workspaceId} />
            ) : null}
            <Button
              size="small"
              icon={<Download size={14} />}
              onClick={() => void handleExport()}
              disabled={total === 0}
            >
              {t('users.actions.export')}
            </Button>
            {canInviteMembers ? (
              <Button
                type="primary"
                size="small"
                icon={<Plus size={14} />}
                onClick={() => setInviteOpen(true)}
              >
                {t('users.actions.invite')}
              </Button>
            ) : null}
          </Space>
        }
      />

      <BulkActionsBar
        count={selected.length}
        entity={t('organizations.users')}
        actions={bulkActions}
        onDeselect={() => setSelected([])}
      />

      <InviteUserDrawer
        open={inviteOpen}
        workspaceId={workspaceId}
        onClose={() => setInviteOpen(false)}
      />

      <UserChangeRoleModal
        workspaceId={workspaceId}
        open={activeAction?.action === 'changeRole'}
        user={activeAction?.action === 'changeRole' ? activeAction.user : null}
        onClose={closeAction}
      />
      <UserEditDrawer
        workspaceId={workspaceId}
        open={activeAction?.action === 'edit'}
        user={activeAction?.action === 'edit' ? activeAction.user : null}
        onClose={closeAction}
      />
      <UserSuspendModal
        workspaceId={workspaceId}
        open={
          activeAction?.action === 'suspend' ||
          activeAction?.action === 'reactivate'
        }
        suspend={activeAction?.action === 'suspend'}
        user={
          activeAction?.action === 'suspend' ||
          activeAction?.action === 'reactivate'
            ? activeAction.user
            : null
        }
        onClose={closeAction}
      />
      <UserArchiveModal
        workspaceId={workspaceId}
        open={
          activeAction?.action === 'archive' ||
          activeAction?.action === 'unarchive'
        }
        mode={activeAction?.action === 'unarchive' ? 'unarchive' : 'archive'}
        user={
          activeAction?.action === 'archive' ||
          activeAction?.action === 'unarchive'
            ? activeAction.user
            : null
        }
        onClose={closeAction}
      />
      <UserDeleteModal
        workspaceId={workspaceId}
        open={activeAction?.action === 'delete'}
        user={activeAction?.action === 'delete' ? activeAction.user : null}
        onClose={closeAction}
      />
      <UserResendInviteModal
        workspaceId={workspaceId}
        open={activeAction?.action === 'resend'}
        user={activeAction?.action === 'resend' ? activeAction.user : null}
        onClose={closeAction}
      />
      <UserConfirmActionModal
        workspaceId={workspaceId}
        open={activeAction?.action === 'revokeInvite'}
        action="revokeInvite"
        user={
          activeAction?.action === 'revokeInvite' ? activeAction.user : null
        }
        onClose={closeAction}
      />

      <UserBulkActionModal
        workspaceId={workspaceId}
        open={bulkAction != null}
        action={bulkAction ?? 'suspend'}
        users={rows
          .filter(({ record }) => selected.includes(record.id))
          .map(({ record }) => record)}
        onClose={() => setBulkAction(null)}
        onSuccessIds={(ids) =>
          setSelected((prev) => prev.filter((id) => !ids.includes(id)))
        }
      />
    </Flex>
  )
}

export const WorkspaceUsersPage = () => (
  <FilterLabelsProvider>
    <WorkspaceUsersPageInner />
  </FilterLabelsProvider>
)
